import * as React from 'react'
import UnifiedRecipeCard, {
  type RecipeCardProps,
  type RecipeCardVariant,
  type RecipeCardSize,
} from './UnifiedRecipeCard'

export type RecipeCardContext = 'discover' | 'plans' | 'myRecipes' | 'favorites'

type BaseProps = Omit<RecipeCardProps, 'variant' | 'size' | 'features' | 'onViewDetails' | 'onAddToPlan' | 'onLike' | 'onEdit'> & {
  context: RecipeCardContext
  isLiked?: boolean
  onView?: (id: string) => void
  onAddToPlan?: (id: string, dayIndex: number, mealType: string) => void
  onLike?: (id: string) => void
  onEdit?: (id: string) => void
  // Optional per-usage overrides
  variantOverride?: RecipeCardVariant
  sizeOverride?: RecipeCardSize
  featuresOverride?: RecipeCardProps['features']
}

function configFor(context: RecipeCardContext): {
  variant: RecipeCardVariant
  size: RecipeCardSize
  features: NonNullable<RecipeCardProps['features']>
  showDescription: boolean
  reserveDescription: boolean
} {
  switch (context) {
    case 'discover':
      return {
        variant: 'detailed',
        size: 'md',
        features: {
          like: true,
          addToPlan: false,
          mealPicker: true,
          rating: true,
          difficulty: true,
          tags: true,
          personalization: false,
        },
        showDescription: true,
        reserveDescription: true,
      }
    case 'plans':
      return {
        variant: 'compact',
        size: 'md',
        features: {
          like: true,
          addToPlan: false,
          rating: false,
          difficulty: true,
          tags: false,
          personalization: false,
        },
        showDescription: false,
        reserveDescription: false,
      }
    case 'favorites':
      return {
        variant: 'detailed',
        size: 'md',
        features: {
          like: true,
          addToPlan: false,
          rating: false,
          difficulty: true,
          tags: false,
          personalization: false,
        },
        showDescription: false,
        reserveDescription: false,
      }
    case 'myRecipes':
    default:
      return {
        variant: 'detailed',
        size: 'md',
        features: {
          like: true,
          addToPlan: false,
          rating: false,
          difficulty: true,
          tags: false,
          personalization: false,
        },
        showDescription: true,
        reserveDescription: true,
      }
  }
}

export function ContextRecipeCard({
  context,
  onView,
  onAddToPlan,
  onLike,
  onEdit,
  isLiked,
  variantOverride,
  sizeOverride,
  featuresOverride,
  ...rest
}: BaseProps) {
  const base = configFor(context)
  const variant = variantOverride ?? base.variant
  const size = sizeOverride ?? base.size
  const features = { ...base.features, ...(featuresOverride || {}) }

  return (
    <UnifiedRecipeCard
      {...rest}
      variant={variant}
      size={size}
      features={features}
      isLiked={isLiked}
      onViewDetails={onView}
      onAddToPlan={onAddToPlan}
      onLike={onLike}
      onEdit={onEdit}
      showDescription={base.showDescription}
      reserveDescription={base.reserveDescription}
    />
  )
}

export default ContextRecipeCard
